import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { Stack } from 'ui/stack';
import { Switch } from 'ui/switch';
import { Select } from 'ui/select';
import { mergeSx } from 'ui/merge-sx';

export interface YearOption {
  value: number;
  label: string;
}

export const generateYearOptions = ({
  futureYears,
  pastYears,
}: {
  futureYears: number;
  pastYears: number;
}): YearOption[] => {
  const currentYear = new Date().getFullYear();
  const range = {
    start: currentYear + futureYears,
    end: currentYear - pastYears,
  };

  const totalYears = range.start - range.end + 1;

  return Array.from({ length: totalYears }, (_, index) => {
    const year = range.start - index;

    return {
      value: year,
      label: String(year),
    };
  });
};

const FILTERS_SX: SxProps<Theme> = { alignItems: 'center', mb: 2 };

interface GoalsFiltersProps {
  showOnlyDefined: boolean;
  setShowOnlyDefined: (value: boolean) => void;
  selectedYear: string | number;
  setSelectedYear: (value: number | string) => void;
  sx?: SxProps<Theme>;
}

const GoalsFilters = ({
  showOnlyDefined,
  setShowOnlyDefined,
  selectedYear,
  setSelectedYear,
  sx,
}: GoalsFiltersProps): React.JSX.Element => {
  const years = generateYearOptions({ futureYears: 3, pastYears: 2 });

  return (
    <Stack direction="row" sx={mergeSx(FILTERS_SX, sx)}>
      <Select<YearOption>
        options={years}
        value={years.find((year) => year.value === Number(selectedYear))}
        onChange={(option) => {
          if (option && !Array.isArray(option)) {
            setSelectedYear(option.value);
          }
        }}
        sx={{ width: 112, mr: 2 }}
        aria-label="Select year"
        data-testid="fiscal-year-select"
      />
      <Switch
        label="Show Only Defined Goals"
        checked={showOnlyDefined}
        onChange={(_event, checked) => setShowOnlyDefined(checked)}
        data-testid="goals-matrix-filters-toggle"
      />
    </Stack>
  );
};

export default GoalsFilters;
